import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { X } from 'lucide-react';
import { fetchConnectors, type ConnectorStatus } from '../../../lib/connectors';
import { connectorsPath } from '../../../lib/connectors-route';
import { queryKeys } from '../../../lib/query-client';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { shopifyAdminUrl, shopifyHealthReasonKey, type ShopifyHealth } from '../../../lib/shopify-health';
import { Button, Card, IconButton } from '../ui';
import { ShopifySourceLine } from './ShopifySourceLine';

export function ShopifyConnectionHealth({ health }: { health: ShopifyHealth }) {
  const { t } = useTranslation();
  const reason = shopifyHealthReasonKey(health);
  const openShopify = health.state === 'store_paused' || health.state === 'store_unavailable';
  const adminUrl = openShopify ? shopifyAdminUrl(health) : undefined;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <ShopifySourceLine
        source={{
          provider: 'shopify',
          storeDomain: health.storeDomain,
          storeName: health.storeName,
          readAt: health.lastSuccessfulReadAt,
          status: 'complete',
        }}
      />
      {reason && <div style={{ fontSize: 13 }}>{t(reason)}</div>}
      {adminUrl && (
        <Button variant="outlined" size="small" onClick={() => window.open(adminUrl, '_blank', 'noopener,noreferrer')}>
          {t('connectors.shopify.open')}
        </Button>
      )}
    </div>
  );
}

export function shopifyReconnectBannerKey(connector: ConnectorStatus): string | undefined {
  if (connector.toolkit !== 'shopify-direct' || connector.needsReconnect !== true || connector.shopifyHealth?.state !== 'reconnect_required')
    return undefined;
  const connectionId = connector.accounts?.[0]?.id;
  return connectionId ? `shopify-reconnect:${connectionId}:${connector.shopifyHealth.storeDomain}` : undefined;
}

export function ShopifyReconnectBanner() {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const { data, isError } = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 30_000 });
  const connector = !isError && data?.enabled ? data.connectors.find((row) => shopifyReconnectBannerKey(row)) : undefined;
  const key = connector && shopifyReconnectBannerKey(connector);
  const [dismissed, setDismissed] = useState<string>();
  useEffect(() => {
    if (!key) return;
    try {
      if (window.sessionStorage.getItem(key) === 'dismissed') setDismissed(key);
    } catch {
      console.warn('Shopify session dismissal could not be read');
    }
  }, [key]);
  if (!connector?.shopifyHealth || !key || dismissed === key) return null;
  return (
    <Card role="status" data-testid="shopify-reconnect-banner" style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ flex: 1 }}>
        <ShopifyConnectionHealth health={{ ...connector.shopifyHealth, state: 'reconnect_required' }} />
        <Button size="small" onClick={() => void router.push(`${basePath}${connectorsPath({ target: 'shopify-direct' })}`)}>
          {t('common.reconnect')}
        </Button>
      </div>
      <IconButton
        size="tiny"
        aria-label={t('common.close')}
        onClick={() => {
          setDismissed(key);
          try {
            window.sessionStorage.setItem(key, 'dismissed');
          } catch {
            console.warn('Shopify session dismissal could not be stored');
          }
        }}
      >
        <X size={16} />
      </IconButton>
    </Card>
  );
}
